import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FuseLoading from '@fuse/core/FuseLoading';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Button, Dialog, DialogContent, DialogTitle, DialogActions } from '@material-ui/core';

import { useForm, FormProvider } from 'react-hook-form';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';
import PiWriterService from 'app/admin/piWriter/service/PiWriterService';
import { showMessage } from 'app/store/fuse/messageSlice';

import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import EditFormCheckboxField from 'modules/ui/editform/fields/EditFormCheckboxField';

const CHECKBOX_AVAILABLE = false;

const _isTruthySuccessful = (res) => Boolean(res && (res.successful === true || res.successful === 'true' || res.successful === 1));
const _extractMessagesText = (res) => {
    const m = res?.messages;
    if (!m) return null;
    if (typeof m === 'string') return m.trim() || null;
    if (Array.isArray(m)) return m.filter(Boolean).map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n') || null;
    if (typeof m === 'object') {
        const vals = Object.values(m).filter(v => v != null && v !== '');
        if (!vals.length) return null;
        return vals.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n') || null;
    }
    return null;
};

export default function ApprovePopup() {
    const { setPopup, optimizationPipelineData, unitOptimizerLogDataList, ts } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);
    const dispatch = useDispatch();

    const piWriterId = optimizationPipelineData?.piWriter ?? null;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const methods = useForm({
        defaultValues: { items: [] },
        mode: 'onChange'
    });

    useEffect(() => {
        let cancelled = false;

        if (!piWriterId) {
            methods.reset({ items: [] });
            setLoading(false);
            return () => { cancelled = true; };
        }
        setLoading(true);

        const selectedEntry = Array.isArray(unitOptimizerLogDataList) ? unitOptimizerLogDataList.find(it => it?.ts === ts) : null;
        const bestDefaults = selectedEntry?.best_state || null;
        const baseDefaults = selectedEntry?.base_state || null;

        PiWriterService.getInputAttributeNames(piWriterId)
            .then((names) => {
                if (cancelled) return;
                const rows = Array.isArray(names) ? names.map((n) => ({
                    name: n,
                    value: (bestDefaults && Object.prototype.hasOwnProperty.call(bestDefaults, n)) ? bestDefaults[n] : (baseDefaults && Object.prototype.hasOwnProperty.call(baseDefaults, n)) ? baseDefaults[n] : null,
                    enabled: true
                })) : [];
                methods.reset({ items: rows });
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[ApprovePopup] getInputAttributeNames error:', err);
                methods.reset({ items: [] });
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [piWriterId, unitOptimizerLogDataList, ts]);

    const handleCancel = () => setPopup?.(null);

    const handleSave = async (formValues) => {
        if (!ts) { dispatch(showMessage({ message: 'Missing timestamp.' })); return; }
        if (!piWriterId) { dispatch(showMessage({ message: 'Missing PI writer id.' })); return; }

        const payload = {};
        let hasError = false;

        if (formValues?.items?.length) {
            formValues.items.forEach((row, idx) => {
                if (CHECKBOX_AVAILABLE && !row?.enabled) return;
                if (!row?.name) return;

                if (row?.value === '' || row?.value == null) {
                    methods.setError(`items.${idx}.value`, { type: 'required'/*, message: 'Required'*/ });
                    hasError = true;
                    return;
                }

                const v = typeof row.value === 'number' ? row.value : Number(row.value);
                if (Number.isNaN(v)) {
                    methods.setError(`items.${idx}.value`, { type: 'validate', message: 'Invalid number' });
                    hasError = true;
                    return;
                }

                payload[row.name] = v;
            });
        }

        if (hasError) { dispatch(showMessage({ message: 'Please fill all required values.' })); return; }
        if (Object.keys(payload).length === 0) { dispatch(showMessage({ message: 'No values to write.' })); return; }

        setSaving(true);
        try {
            const res = await PiWriterService.writeDataEntry(piWriterId, ts, payload);

            const msgText = _extractMessagesText(res);
            if (_isTruthySuccessful(res)) {
                dispatch(showMessage({ message: msgText || 'Data has been written successfully.' }));
                setPopup?.(null);
            } else {
                dispatch(showMessage({ message: msgText || 'Failed to write data.' }));
            }
        } catch (err) {
            console.error('[ApprovePopup] writeData error:', err);
            dispatch(showMessage({ message: 'Failed to write data.' }));
        } finally {
            setSaving(false);
        }
    };

    const itemsLen = methods.getValues('items')?.length || 0;

    return (
        <ThemeProvider theme={mainTheme}>

            <Dialog open fullWidth maxWidth='sm'>

                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className='flex items-center justify-between gap-32 px-32'>
                        <div className='whitespace-nowrap'>Approve</div>
                    </div>
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ maxHeight: '520px' }}>

                    {loading && <FuseLoading />}

                    {!loading && itemsLen === 0 && (<div className='text-sm text-gray-500'>No attributes found.</div>)}

                    {!loading && itemsLen > 0 && (
                        <div className='flex-1 overflow-auto pr-4'>
                            <FormProvider {...methods}>
                                <EditFormGridComponent
                                    config={{ topic: 'model' }}
                                    fieldConfig={{
                                        key: 'items',
                                        component: EditFormGridComponent,
                                        modifyOnly: true,
                                        readOnly: true,
                                        defaultValue: { name: null, value: null, enabled: true },
                                        fields: [
                                            { key: 'name', text: 'Attribute', component: EditFormTextField, required: true, readOnly: true },
                                            { key: 'value', text: 'Value', component: EditFormTextField, required: true, type: 'number' },
                                            ...(CHECKBOX_AVAILABLE ? [{ key: 'enabled', text: 'Enabled', component: EditFormCheckboxField, required: false }] : [])
                                        ]
                                    }}
                                />
                            </FormProvider>
                        </div>
                    )}
                </DialogContent>

                <DialogActions className='justify-between border-t p-20'>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button color='primary' variant='contained' onClick={methods.handleSubmit(handleSave)} autoFocus disabled={loading || saving || !piWriterId || !ts}>{saving ? 'Writing' : 'Write data'}</Button>
                </DialogActions>

            </Dialog>

        </ThemeProvider>
    );
}
